import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { configModel } from "src/app/interfaces/config.interface";
import { MenuOptionModel } from "src/app/interfaces/sidemenu-interface";
import { EventsService } from "../event/event.service";
import { Platform } from "@ionic/angular";
import { AppstorageTsService } from "../appstorage/appstorage.ts.service";
import { isUndefined, stringToNumber } from "src/app/services/codeUtils";
import { MarketingService } from "../marketing/marketing.service";

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  private config: any;
  private environment: any;
  private externalLinks: any = [];
  private footerUrl: any;
  private homePageUrl: any;
  private hideSportsLoader: boolean = false;

  constructor(
    private http: HttpClient,
    private eventsService: EventsService,
    private platform: Platform,
    private appstorageTsService: AppstorageTsService,
    // private marketingService: MarketingService

  ) { }

  private configPromise: Promise<any> = new Promise((resolve, reject) => {
    this.getConfigFromEnv(resolve, reject);
  });

  /**
   * Checks if a specified feature is turned on in the provided configuration map.
   * 
   * This method evaluates whether a feature is enabled or disabled based on a
   * configuration map. The configuration map may contain string values representing
   * the feature state, including:
   *  - "0" => false
   *  - "1" => true
   *  - null => false
   *  - undefined => false
   * 
   * June 2024 update: An optional `defaultValue` parameter was introduced to provide
   * a default setting when the feature flag is undefined.
   * 
   * @param config - The configuration map to be evaluated.
   * @param configName - The name of the feature to check within the configuration map.
   * @param defaultValue - (optional) Specifies a default value for the flag when the flag is undefined.
   * @returns A boolean indicating whether the feature is turned on (true) or off (false).
   */
  public isFeatureON = (config, configName: string, defaultValue?: boolean): boolean => {
    if (typeof defaultValue !== "undefined" && typeof config[configName] === "undefined") {
      return defaultValue;
    }
    return (
      config[configName] && parseInt(config[configName]) !== 0
    );
  }


  private getConfigFromEnv(
    resolve: (value: any) => void,
    reject: (value: any) => void
  ) {
    //trying localStorage logic as of now, to check clientApp - mainly authenticated case
    //console.log("%cStardust : At revision: 114092 on 1 Mar at 10:25 AM GLI", "color: red; font-weight: bold");

    this.http.get("/assets/environment.config").subscribe((data) => {
      this.environment = data;

      this.adjustClientApp().then(() => {
        let config_url =
          this.environment.webservices_url +
          "/configuration/get-config-data?clientapp=" +
          this.environment.clientapp;
        this.getConfigFromUrl(config_url, resolve);
      });
    });
  }

  private getConfigFromUrl(config_url: string, resolve: (value: any) => void) {
    this.http.get<configModel>(config_url).subscribe((config) => {
      let configData: Object = {};

      config.payload.map(function (data) {
        configData[data.name] = data.value;
      });

      this.config = configData;

      this.callGetExternalLinks().then((response: any) => {
        this.externalLinks = response;

        //marketing initialize
        this.eventsService.publish("event:affiliation:initialize");

        resolve(true);
      });
    });
  }

  getCampaignBanerImgUrl(data) {
    let imgLocation = `${this.getBaseUrl()}/assets/`;
    return {
      sm: `${imgLocation}notifications/${data.banner_image_small}`,
      md: `${imgLocation}notifications/${data.banner_image_medium}`,
      lg: `${imgLocation}notifications/${data.banner_image_large}`,
    };
  }

  reloadConfig(env = "") {
    console.log("reloadConfig", env)
    return new Promise((resolve, reject) => {
      let path = `/assets/environment.config`
      if (env != "" && env != "redirect")
        path = `/assets/environment.${env}.config`

      this.http.get(path).subscribe((data) => {
        this.environment = data;

        this.adjustClientApp().then(() => {
          let config_url = this.environment.webservices_url + "/configuration/get-config-data?clientapp=" + this.environment.clientapp;
          this.http.get<configModel>(config_url).subscribe((config) => {
            let configData: Object = {};

            config.payload.map(function (data) {
              configData[data.name] = data.value;
            });
            configData["WEBSERVICES_URL"] = this.environment.webservices_url;

            this.config = configData;

            this.callGetExternalLinks().then((response: any) => {
              this.externalLinks = response;
              this.eventsService.publish("event:config:reload");
              resolve(true);
            });
            //this.events.publish('event:affiliation:initialize');
            //this.events.publish('event:config:final');          // this event fires *after* environment-selector phase is done -- very useful!
          });
        });
      });
    });
  }
  clientAppWithNoJurisdiction() {
    if (this.environment?.client_app_with_no_jursdiction && this.environment.client_app_with_no_jursdiction) {
      return true
    } else {
      return false
    }
  }

  //TODO: need to test all the cases
  adjustClientApp(): Promise<any> {
    return new Promise((resolve, reject) => {

      this.consoleLogPlatformParameters();

      const OS = this.platform.is("ios") ? "iOS" : "Android";
      const DEVICE = this.platform.is("tablet") ? "Tablet" : "Phone";
      const strSiteSkin =
        this.environment.siteskin != null ? this.environment.siteskin : "";

      if (this.platform.is("capacitor")) {
        console.log("clientapp change needed for mobile app");
        this.appstorageTsService.getJurisdiction().then(
          (jurisdictionResponse) => {
            const version: string = "1.0";
            console.log("app.version = ", version);

            const jurisdiction = this.clientAppWithNoJurisdiction() ? "NJ" :
              (!!jurisdictionResponse.value
                ? jurisdictionResponse.value
                : "NJ");

            this.environment.clientapp = DEVICE + OS + "CasinoMO" + jurisdiction + "-" + version;
            console.log("mobile app clientapp is now", this.environment.clientapp);
            this.eventsService.publish("event:config:clientapp");
            resolve(true);
          }, () => {
            console.log("clientapp is not changed properly! only adding siteskin prefix...", this.environment.clientapp);

            let clientapp: string = this.environment.clientapp;
            this.environment.clientapp = strSiteSkin + clientapp;

            this.eventsService.publish("event:config:clientapp");

            resolve(true);
          }
        );
      } else if (this.platform.is("ios") || this.platform.is("android")) {
        let clientapp: string = this.environment.clientapp;

        console.log("clientapp change needed for mobile web");

        this.environment.clientapp = DEVICE + OS + clientapp;

        console.log("mobile web clientapp is now", this.environment.clientapp);

        this.eventsService.publish("event:config:clientapp");

        resolve(true);
      } else {
        // nothing on web
        resolve(true);
      }
    });
  }


  private consoleLogPlatformParameters() {
    if (this.platform.platforms()) console.log("platforms =", this.platform.platforms());
    if (this.platform.is("capacitor")) console.log("capacitor =", this.platform.is("capacitor"));
    if (this.platform.is("ios")) console.log("ios =", this.platform.is("ios"));
    if (this.platform.is("android")) console.log("android =", this.platform.is("android"));
    if (this.platform.is("tablet")) console.log("tablet =", this.platform.is("tablet"));
  }

  isLoaded(): Promise<any> {
    return this.configPromise;
  }

  private callGetExternalLinks() {
    const clientApp: any = this.environment.clientapp;
    let serviceURL =
      this.environment.webservices_url +
      "/configuration/get-external-links?clientapp=" +
      clientApp;
    return this.http.get<configModel>(serviceURL).toPromise();
  }

  getConfig() {
    return this.config;
  }

  getEnv() {
    return this.environment;
  }

  getSelectionText() {
    return this.config?.FEATURE_SELECTION_TEXT
  }

  loadCDNAsset(path) {
    let v = new Date().getTime();
    return `${path}?v=${v}`;
  }

  getClientApp() {
    return this.environment.clientapp;
  }

  getDeafaultRoute() {
    return this.environment.default_route;
  }

  getState() {
    return this.config.FEATURE_STATE_NAME;
  }

  public getBaseUrl() {
    return this.config.CDN_BASEURL;
  }

  public getLobbyImagePath(imageName) {
    return `${this.getBaseUrl()}/assets/social/games/${imageName}`;
  }

  isSportsApp() {
    return this.config.FEATURE_IS_SPORTS_APP === "1";
  }

  isBiometricEnabled() {
    return this.config.FEATURE_APP_BIOMETRICS_ON === "1";
  }

  getExternalLinks() {
    return this.externalLinks;
  }

  getXtremeWebSdkUrl() {
    return !isUndefined(this.config.FEATURE_XTREME_WEB_SDK_URL)
      ? this.config.FEATURE_XTREME_WEB_SDK_URL
      : "";
  }

  getXtremeIosAppKey() {
    return !isUndefined(this.config.FEATURE_XTREME_IOS_KEY)
      ? this.config.FEATURE_XTREME_IOS_KEY
      : "";
  }

  getXtremeAndroidAppKey() {
    return !isUndefined(this.config.FEATURE_XTREME_ANDROID_KEY)
      ? this.config.FEATURE_XTREME_ANDROID_KEY
      : "";
  }

  getXtremeGcmProjectNumber() {
    return !isUndefined(this.config.FEATURE_XTREME_GCM_NR)
      ? this.config.FEATURE_XTREME_GCM_NR
      : "";
  }

  getXtpPlayerPrefix() {
    return !isUndefined(this.config.FEATURE_XTP_PLAYER_PREFIX) ? this.config.FEATURE_XTP_PLAYER_PREFIX : "";
  }

  getPixelKey() {
    return !isUndefined(this.config.FEATURE_META_KEY) ? this.config.FEATURE_META_KEY : "";
  }

  getGTMKey() {
    return !isUndefined(this.config.FEATURE_GTM_KEY) ? this.config.FEATURE_GTM_KEY : "";
  }

  getGoogleAnalyticsKey() {
    return !isUndefined(this.config.FEATURE_GANALYTICS_KEY) ? this.config.FEATURE_GANALYTICS_KEY : "";
  }

  getGoogleABTestingKey() {
    return !isUndefined(this.config.FEATURE_GABTEST_KEY) ? this.config.FEATURE_GABTEST_KEY : "";
  }

  //config utils

  public isEnabled(key: string, defaultValue = false): boolean {
    const configValue = this.config?.[key];
    return configValue === '1' ? true : configValue === undefined ? defaultValue : false;
  }

  public numberConfig(key: string, defaultValue?: number): number | undefined {
    const raw = this.config?.[key]?.trim();
    const num = parseFloat(raw);
    return isNaN(num) ? defaultValue : num;
  }


}
